import { Suspense } from 'react';
import { Link, useParams } from 'react-router-dom';
import { parseAsInteger, useQueryState } from 'nuqs';
import { User } from 'lucide-react';
import { Card, CardContent } from '@reflex/ui/components/ui/card';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { CopyButton } from '@reflex/ui/components/shared/CopyButton';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { EmailNotVerifiedNotice } from '@/components/auth/EmailNotVerifiedNotice';
import { useAuth } from '@/hooks/auth/useAuth';
import { useUser } from '@/hooks/auth/useUser';
import { useProfileStats } from '@/hooks/auth/useProfileStats';
import { usePlugins } from '@/hooks/config/usePlugins';
import { ContributionHeatmap } from '@reflex/ui/components/chart/ContributionHeatmap';
import { TopRepositories } from '@/components/dashboard/TopRepositories';
import { formatDateLong, getProviderLabel } from '@reflex/ui/lib/format';
import type { User as UserType } from '@/lib/types';
import { getUserDisplayName } from '@/lib/agent-utils';
import { ROUTES } from '@/lib/routes';

export function ProfilePage() {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user: currentUser, authEnabled } = useAuth();
  const { data: userData, isLoading: userLoading, error: userError } = useUser(paramUserId);
  const targetUserId = paramUserId ?? currentUser?.id;
  const isOwnProfile = !paramUserId || paramUserId === currentUser?.id;
  const user: UserType | null = isOwnProfile ? (currentUser ?? null) : (userData ?? null);
  const [contributionYear, setContributionYear] = useQueryState(
    'year',
    parseAsInteger.withDefault(new Date().getFullYear()),
  );
  const { data: profileStats, isLoading: profileStatsLoading } = useProfileStats(
    targetUserId ?? undefined,
    contributionYear,
  );
  // Plugin-contributed profile sections (e.g. plugin-costs "Your usage").
  // Only rendered on the viewer's own profile — visiting another user's
  // page should not leak per-user data through a plugin widget.
  const { profileSections } = usePlugins();

  if (!authEnabled) {
    return <EmptyState variant="fullHeight" title="Authentication is disabled." />;
  }

  if (!currentUser) {
    return <EmptyState variant="fullHeight" title="You are not signed in." />;
  }

  if (paramUserId && userLoading) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  if (paramUserId && (userError || !userData)) {
    return (
      <EmptyState
        variant="fullHeight"
        title="User not found."
        action={
          <Button asChild variant="link">
            <Link to={ROUTES.profile}>Back to your profile</Link>
          </Button>
        }
      />
    );
  }

  if (!user) return null;

  return (
    <PageLayout maxWidth="5xl">
      <PageHeader
        icon={<User className="h-5 w-5" />}
        title={isOwnProfile ? 'Profile' : getUserDisplayName(user)}
        description={isOwnProfile ? 'Your account activity and settings' : undefined}
      />

      {/* Two-column layout on desktop */}
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Left: Identity & settings (sidebar on desktop) */}
        <div className="shrink-0 space-y-4 lg:w-64 lg:flex-shrink-0">
          <Card className="py-4 lg:py-6">
            <CardContent className="px-4 py-0 lg:px-6">
              <div className="flex items-start gap-3 sm:gap-4">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={`${getUserDisplayName(user)}'s avatar`}
                    className="h-14 w-14 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-border/50 sm:h-16 sm:w-16"
                  />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 sm:h-16 sm:w-16">
                    <User className="h-7 w-7 text-primary sm:h-8 sm:w-8" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <PanelTitle as="p" size="sm" className="truncate">
                    {getUserDisplayName(user)}
                  </PanelTitle>
                  <PageDescription className="mt-0.5 truncate font-mono text-xs">
                    @{user.login}
                  </PageDescription>
                  {isOwnProfile && (
                    <PageDescription className="mt-1 truncate text-xs">
                      {user.email}
                    </PageDescription>
                  )}
                  {isOwnProfile && (
                    <div className="mt-1 flex min-w-0 items-center gap-1">
                      <code
                        className="min-w-0 flex-1 truncate font-mono text-xs text-foreground/85 select-all"
                        data-testid="profile-user-id"
                      >
                        {user.id}
                      </code>
                      <CopyButton
                        text={user.id}
                        ariaLabel={`Copy user id ${user.id}`}
                        copiedAriaLabel="Copied user id"
                      />
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border/50 pt-3">
                    <Badge variant="secondary" className="text-xs font-normal">
                      {getProviderLabel(user.providerId)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Joined {formatDateLong(user.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          {isOwnProfile && user.emailVerified === false && <EmailNotVerifiedNotice />}
        </div>

        {/* Right: Activity (main content) */}
        <div className="min-w-0 flex-1 space-y-6">
          <SectionCard title="Top repositories" description="Repos with the most agent runs">
            {profileStatsLoading ? (
              <LoadingState className="h-32" />
            ) : (
              <TopRepositories
                repositories={profileStats?.topRepositories ?? []}
                totalRuns={profileStats?.totalRuns}
                repoTaggedRuns={profileStats?.repoTaggedRuns}
                showTitle={false}
              />
            )}
          </SectionCard>

          <SectionCard title="Agent runs" description="Agent runs by year">
            {profileStatsLoading ? (
              <LoadingState className="h-32" />
            ) : (
              <ContributionHeatmap
                valuesByDay={profileStats?.agentRunStartsByDay ?? {}}
                year={contributionYear}
                colorScheme="runs"
                onYearChange={(y) => void setContributionYear(y)}
              />
            )}
          </SectionCard>

          {isOwnProfile &&
            profileSections.map((section) => (
              <Suspense key={section.id} fallback={<LoadingState className="h-32" />}>
                <section.component />
              </Suspense>
            ))}
        </div>
      </div>
    </PageLayout>
  );
}
